import { useEffect, useMemo, useState } from 'react';
import type { ProductDetailDto, ProductVariantDto } from '@ecom/contracts';
import type { VariantInput } from '@ecom/sdk';
import { useCatalogStore } from '@/stores/catalog.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count, money } from '@/lib/format';
import { mediaUrl } from '@/lib/media';
import { halalasToRiyals, riyalsToHalalas, thousandthsToUnits, unitsToThousandths } from '@/lib/price-input';
import { COLOR_OPTION, detailLabel, fabricThumb, fabricsOf, optionLabel, type Fabric } from '@/lib/fabrics';
import { valuesOf } from '@/lib/variant-matrix';
import { ToggleField } from '@/components/fields';
import { ChevronDownIcon, PencilIcon, PlusIcon, TrashIcon } from '@/components/icons';
import { Section, Segmented, Sheet, Table, Td, Th, Thumb } from '@/components/page';
import { Alert, Button, ConfirmDialog, Field, IconButton, Input, Pill, Select, cx } from '@/components/ui';

/**
 * A product's variants, organised.
 *
 * A fabric sold in seven colours on four lengths and as a swatch is thirty-five
 * variants; as one flat list they are unreadable. Grouped by colour they are
 * seven blocks of five, each block headed by its swatch, its name and its
 * stock — the way the reference dashboards group variants by their first
 * option. Price and stock are edited in place, because a price fix should not
 * be a form; everything else opens in a panel beside the list.
 *
 * Stock typed here reaches the API as a recount, so the level and its history
 * never disagree; and it is sent only when it was changed, so an open page
 * does not put sold units back on the shelf.
 */

type Grouping = 'colour' | 'none';

/** What an inline cell holds while it differs from what is stored. */
interface CellEdit {
  price?: string;
  stock?: string;
}

/** The price a variant sets for itself, or empty when it follows the base price. */
const ownPrice = (variant: ProductVariantDto, basePrice: number) =>
  variant.price === basePrice ? '' : halalasToRiyals(variant.price);

/**
 * What every update of a variant must carry. The API writes these four on
 * every save — absent is "none", not "unchanged" — so leaving out `isDefault`
 * quietly took the default away from whichever variant was edited.
 */
function keep(variant: ProductVariantDto, basePrice: number): Pick<VariantInput, 'sku' | 'options' | 'price' | 'isDefault'> {
  return {
    sku: variant.sku,
    options: variant.options,
    price: variant.price === basePrice ? null : variant.price,
    isDefault: variant.isDefault,
  };
}

export function VariantsCard({ product }: { product: ProductDetailDto }) {
  const { saving, error, updateVariant, removeVariant, removeVariants } = useCatalogStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  const fabrics = useMemo(() => fabricsOf(product), [product]);
  const hasColour = fabrics.some((fabric) => fabric.color !== null);
  const [grouping, setGrouping] = useState<Grouping>('colour');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [edits, setEdits] = useState<Record<string, CellEdit>>({});
  const [sheet, setSheet] = useState<{ variant: ProductVariantDto | null; colour: string | null } | null>(null);
  const [removing, setRemoving] = useState<ProductVariantDto | null>(null);
  /** Variants picked for a bulk change — "every 10 m roll", "all of this colour". */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkPrice, setBulkPrice] = useState('');
  const [bulkStock, setBulkStock] = useState('');
  const [removingSelected, setRemovingSelected] = useState(false);

  // Picks for variants that no longer exist go with them.
  useEffect(() => {
    setSelected((current) => new Set([...current].filter((id) => product.variants.some((variant) => variant.id === id))));
  }, [product]);

  const pick = (ids: string[], on: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  /** A bulk change fills the cells; the operator still reviews and saves them. */
  const applyToSelected = (patch: CellEdit) =>
    setEdits((current) => {
      const next = { ...current };
      for (const id of selected) next[id] = { ...next[id], ...patch };
      return next;
    });

  // A save returns the whole product; edits it has absorbed are dropped.
  useEffect(() => {
    setEdits((current) => {
      const next: Record<string, CellEdit> = {};
      for (const [id, edit] of Object.entries(current)) {
        const variant = product.variants.find((entry) => entry.id === id);
        if (!variant) continue;
        const kept: CellEdit = {};
        if (edit.price !== undefined && edit.price !== ownPrice(variant, product.basePrice)) kept.price = edit.price;
        if (edit.stock !== undefined && edit.stock !== String(variant.stock)) kept.stock = edit.stock;
        if (kept.price !== undefined || kept.stock !== undefined) next[id] = kept;
      }
      return next;
    });
  }, [product]);

  const changed = Object.keys(edits);
  const grouped = grouping === 'colour' && hasColour;
  const groups: { fabric: Fabric | null; variants: ProductVariantDto[] }[] = grouped
    ? [
        ...fabrics.filter((fabric) => fabric.color !== null).map((fabric) => ({ fabric, variants: fabric.variants })),
        // Variants with no colour on a product that has colours: shown, not lost.
        ...(product.variants.some((variant) => !variant.options[COLOR_OPTION])
          ? [{ fabric: null, variants: product.variants.filter((variant) => !variant.options[COLOR_OPTION]) }]
          : []),
      ]
    : [{ fabric: null, variants: product.variants }];

  const edit = (variant: ProductVariantDto, patch: CellEdit) =>
    setEdits((current) => ({ ...current, [variant.id]: { ...current[variant.id], ...patch } }));

  async function saveEdits() {
    for (const id of changed) {
      const variant = product.variants.find((entry) => entry.id === id);
      const cell = edits[id];
      if (!variant || !cell) continue;
      const price = cell.price !== undefined ? (cell.price.trim() ? riyalsToHalalas(cell.price) : null) : undefined;
      if (price === null && cell.price?.trim()) continue; // unreadable — left in place, marked
      const stock = cell.stock !== undefined && /^\d+$/.test(cell.stock.trim()) ? Number(cell.stock) : undefined;
      const ok = await updateVariant(variant.id, {
        ...keep(variant, product.basePrice),
        ...(price !== undefined ? { price } : {}),
        ...(stock !== undefined ? { stock } : {}),
      });
      if (!ok) return;
    }
  }

  const colourCount = fabrics.filter((fabric) => fabric.color !== null).length;

  return (
    <Section
      title={t('variants')}
      subtitle={
        hasColour
          ? t('variantsSummary', { variants: count(product.variants.length, locale), colours: count(colourCount, locale) })
          : t('variantStockHint')
      }
      flush
      action={
        <>
          {hasColour ? (
            <Segmented
              label={t('variants')}
              value={grouping}
              onChange={setGrouping}
              items={[
                { id: 'colour', label: t('groupByColour') },
                { id: 'none', label: t('groupNone') },
              ]}
            />
          ) : null}
          <Button variant="secondary" size="sm" onClick={() => setSheet({ variant: null, colour: null })}>
            <PlusIcon className="size-4" />
            {t('addVariant')}
          </Button>
        </>
      }
    >
      {error && !sheet ? (
        <div className="px-5 pb-3">
          <Alert>{error}</Alert>
        </div>
      ) : null}

      {selected.size > 0 ? (
        <div className="flex flex-wrap items-center gap-2 border-t border-line bg-brand-50/60 px-5 py-2.5">
          <span data-numeric className="me-2 text-sm font-medium text-brand-700">
            {t('pickerSelected', { count: count(selected.size, locale) })}
          </span>
          <Input
            dir="ltr"
            inputMode="decimal"
            aria-label={t('price')}
            placeholder={t('price')}
            value={bulkPrice}
            onChange={(event) => setBulkPrice(event.target.value)}
            className="h-8 min-h-8 w-24"
          />
          <Button
            variant="secondary"
            size="sm"
            disabled={bulkPrice.trim() !== '' && riyalsToHalalas(bulkPrice) === null}
            onClick={() => applyToSelected({ price: bulkPrice.trim() })}
          >
            {t('setPrice')}
          </Button>
          {/* A digital variant's stock is the system's (ADR-077). */}
          {product.kind === 'PHYSICAL' ? (
            <>
              <Input
                dir="ltr"
                inputMode="numeric"
                aria-label={t('stock')}
                placeholder={t('stock')}
                value={bulkStock}
                onChange={(event) => setBulkStock(event.target.value.replace(/[^0-9]/g, ''))}
                className="h-8 min-h-8 w-20"
              />
              <Button variant="secondary" size="sm" disabled={bulkStock === ''} onClick={() => applyToSelected({ stock: bulkStock })}>
                {t('setStock')}
              </Button>
            </>
          ) : null}
          <span className="ms-auto flex gap-2">
            <Button variant="ghost" size="sm" className="text-danger" onClick={() => setRemovingSelected(true)}>
              <TrashIcon className="size-3.5" />
              {t('remove')}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
              {t('clearSelection')}
            </Button>
          </span>
        </div>
      ) : null}

      <div className="overflow-x-auto border-t border-line">
        <Table>
          <thead>
            <tr>
              <Th className="w-10">
                <input
                  type="checkbox"
                  aria-label={t('selectAll')}
                  checked={selected.size > 0 && selected.size === product.variants.length}
                  ref={(input) => {
                    if (input) input.indeterminate = selected.size > 0 && selected.size < product.variants.length;
                  }}
                  onChange={(event) => pick(product.variants.map((variant) => variant.id), event.target.checked)}
                  className="size-4"
                />
              </Th>
              <Th>{grouped ? t('variant') : t('variants')}</Th>
              <Th>{t('sku')}</Th>
              <Th className="w-36">{t('price')}</Th>
              <Th className="w-28">{t('stock')}</Th>
              <Th align="end" className="w-24">
                <span className="sr-only">{t('editVariant')}</span>
              </Th>
            </tr>
          </thead>
          {groups.map(({ fabric, variants }) => {
            const groupKey = fabric?.key ?? '—';
            const folded = collapsed.has(groupKey);
            const stock = variants.reduce((sum, variant) => sum + variant.stock, 0);
            return (
              <tbody key={groupKey}>
                {grouped ? (
                  <tr className="bg-surface-sunken/70">
                    <td colSpan={6} className="border-b border-line px-4 py-2 first:ps-5">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          aria-label={t('selectGroup', { name: fabric?.label ?? t('noColour') })}
                          checked={variants.every((variant) => selected.has(variant.id))}
                          onChange={(event) => pick(variants.map((variant) => variant.id), event.target.checked)}
                          className="size-4 shrink-0"
                        />
                        <button
                          type="button"
                          aria-expanded={!folded}
                          onClick={() =>
                            setCollapsed((current) => {
                              const next = new Set(current);
                              if (next.has(groupKey)) next.delete(groupKey);
                              else next.add(groupKey);
                              return next;
                            })
                          }
                          className="flex min-w-0 flex-1 items-center gap-3 text-start"
                        >
                          <ChevronDownIcon className={cx('size-4 shrink-0 text-ink-faint transition-transform', folded && '-rotate-90 rtl:rotate-90')} />
                          {fabric ? (
                            <Thumb src={mediaUrl(fabricThumb(product, fabric))} swatch={fabric.swatchHex} size="sm" className="rounded-full" />
                          ) : null}
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-semibold text-ink">{fabric?.label ?? t('noColour')}</span>
                            <span data-numeric className="block text-xs text-ink-muted">
                              {t('colourStats', { variants: count(variants.length, locale), stock: count(stock, locale) })}
                            </span>
                          </span>
                        </button>
                        {fabric?.color ? (
                          <Button variant="ghost" size="sm" onClick={() => setSheet({ variant: null, colour: fabric.color })}>
                            <PlusIcon className="size-3.5" />
                            {t('addVariantToColour')}
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ) : null}
                {folded
                  ? null
                  : variants.map((variant) => {
                      const cell = edits[variant.id] ?? {};
                      const priceValue = cell.price ?? ownPrice(variant, product.basePrice);
                      const stockValue = cell.stock ?? String(variant.stock);
                      const badPrice = Boolean(cell.price?.trim()) && riyalsToHalalas(cell.price ?? '') === null;
                      const colourLabel = variant.options[COLOR_OPTION]
                        ? optionLabel(product, COLOR_OPTION, variant.options[COLOR_OPTION])
                        : null;
                      const label = grouped
                        ? detailLabel(product, variant) || colourLabel || variant.sku
                        : [colourLabel, detailLabel(product, variant)].filter(Boolean).join(' · ') || variant.sku;
                      return (
                        <tr
                          key={variant.id}
                          className={cx(
                            'transition-colors hover:bg-surface-sunken/50',
                            selected.has(variant.id) ? 'bg-brand-50/60' : cell.price !== undefined || cell.stock !== undefined ? 'bg-brand-50/40' : '',
                          )}
                        >
                          <Td>
                            <input
                              type="checkbox"
                              aria-label={variant.sku}
                              checked={selected.has(variant.id)}
                              onChange={(event) => pick([variant.id], event.target.checked)}
                              className="size-4"
                            />
                          </Td>
                          <Td className={grouped ? 'ps-8' : undefined}>
                            <span className="flex flex-wrap items-center gap-2">
                              <span className="text-sm text-ink">{label}</span>
                              {variant.isDefault ? <Pill tone="info">{t('defaultVariant')}</Pill> : null}
                              {variant.isSample ? <Pill tone="success">{t('sampleVariant')}</Pill> : null}
                              {variant.maxPerOrder ? (
                                <Pill>{t('limitPerOrder').replace('{count}', String(variant.maxPerOrder))}</Pill>
                              ) : null}
                            </span>
                            {product.measureUnit && variant.measureQuantity ? (
                              <span data-numeric dir="ltr" className="mt-0.5 block text-[11px] text-ink-faint">
                                {thousandthsToUnits(variant.measureQuantity)} {t(`unit_${product.measureUnit}`)}
                                {variant.unitPrice !== null
                                  ? ` · ${money(variant.unitPrice, locale, product.currency)} / ${t(`unit_${product.measureUnit}`)}`
                                  : ''}
                              </span>
                            ) : null}
                          </Td>
                          <Td>
                            <code dir="ltr" className="text-xs text-ink-muted">
                              {variant.sku}
                            </code>
                          </Td>
                          <Td>
                            <Input
                              dir="ltr"
                              inputMode="decimal"
                              aria-label={`${t('price')} — ${variant.sku}`}
                              value={priceValue}
                              invalid={badPrice}
                              placeholder={halalasToRiyals(product.basePrice)}
                              onChange={(event) => edit(variant, { price: event.target.value })}
                              className="h-9 min-h-9 w-28"
                            />
                          </Td>
                          <Td>
                            {product.kind === 'PHYSICAL' ? (
                              <Input
                                dir="ltr"
                                inputMode="numeric"
                                aria-label={`${t('stock')} — ${variant.sku}`}
                                value={stockValue}
                                onChange={(event) => edit(variant, { stock: event.target.value.replace(/[^0-9]/g, '') })}
                                className={cx('h-9 min-h-9 w-20', variant.stock === 0 && cell.stock === undefined && 'text-danger')}
                              />
                            ) : (
                              <DigitalStock kind={product.kind} stock={variant.stock} />
                            )}
                          </Td>
                          <Td align="end">
                            <span className="inline-flex gap-1">
                              <IconButton
                                label={t('editVariant')}
                                onClick={() => setSheet({ variant, colour: variant.options[COLOR_OPTION] ?? null })}
                                className="size-8 border-0"
                              >
                                <PencilIcon className="size-4" />
                              </IconButton>
                              <IconButton
                                label={t('remove')}
                                onClick={() => setRemoving(variant)}
                                className="size-8 border-0 text-ink-faint hover:text-danger"
                              >
                                <TrashIcon className="size-4" />
                              </IconButton>
                            </span>
                          </Td>
                        </tr>
                      );
                    })}
              </tbody>
            );
          })}
        </Table>
      </div>

      {changed.length > 0 ? (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line bg-brand-50/40 px-5 py-3">
          <span className="me-auto text-xs text-ink-muted">{t('variantStockMovement')}</span>
          <Button variant="secondary" size="sm" onClick={() => setEdits({})} disabled={saving}>
            {t('discard')}
          </Button>
          <Button size="sm" onClick={() => void saveEdits()} disabled={saving}>
            {saving ? t('saving') : t('saveVariantChanges', { count: count(changed.length, locale) })}
          </Button>
        </div>
      ) : null}

      <VariantSheet
        open={sheet !== null}
        product={product}
        fabrics={fabrics}
        variant={sheet?.variant ?? null}
        colour={sheet?.colour ?? null}
        onClose={() => setSheet(null)}
      />

      <ConfirmDialog
        open={removing !== null}
        title={t('removeVariantTitle')}
        body={t('removeVariantBody')}
        confirmLabel={t('remove')}
        cancelLabel={t('cancel')}
        busy={saving}
        onConfirm={async () => {
          if (removing) await removeVariant(removing.id);
          setRemoving(null);
        }}
        onCancel={() => setRemoving(null)}
      />

      <ConfirmDialog
        open={removingSelected}
        title={t('removeSelectedTitle', { count: count(selected.size, locale) })}
        body={t('removeSelectedBody')}
        confirmLabel={t('remove')}
        cancelLabel={t('cancel')}
        busy={saving}
        onConfirm={async () => {
          if (await removeVariants([...selected])) setSelected(new Set());
          setRemovingSelected(false);
        }}
        onCancel={() => setRemovingSelected(false)}
      />
    </Section>
  );
}

// ─── One variant, in full ────────────────────────────────────────────────────

interface Draft {
  colour: string;
  /** The options other than colour, as rows — `unit` / `roll-10`. */
  options: { name: string; value: string }[];
  sku: string;
  price: string;
  /** What one unit cost, riyals without VAT (ADR-118); empty for none. */
  costPrice: string;
  stock: string;
  measure: string;
  maxPerOrder: string;
  isSample: boolean;
  isDefault: boolean;
}

function draftFrom(product: ProductDetailDto, variant: ProductVariantDto | null, colour: string | null): Draft {
  if (!variant) {
    // A new variant starts with the option names the product already uses, so
    // "unit" is not retyped (and mistyped) for the fortieth time.
    const names = [...new Set(product.variants.flatMap((entry) => Object.keys(entry.options)))].filter(
      (name) => name !== COLOR_OPTION,
    );
    return {
      colour: colour ?? '',
      options: names.map((name) => ({ name, value: valuesOf(product, name)[0] ?? '' })),
      sku: '',
      price: '',
      costPrice: '',
      stock: '0',
      measure: '',
      maxPerOrder: '',
      isSample: false,
      isDefault: false,
    };
  }
  return {
    colour: variant.options[COLOR_OPTION] ?? '',
    options: Object.entries(variant.options)
      .filter(([name]) => name !== COLOR_OPTION)
      .map(([name, value]) => ({ name, value })),
    sku: variant.sku,
    price: variant.price === product.basePrice ? '' : halalasToRiyals(variant.price),
    costPrice: variant.costPrice === null || variant.costPrice === undefined ? '' : halalasToRiyals(variant.costPrice),
    stock: String(variant.stock),
    measure: thousandthsToUnits(variant.measureQuantity),
    maxPerOrder: variant.maxPerOrder ? String(variant.maxPerOrder) : '',
    isSample: variant.isSample,
    isDefault: variant.isDefault,
  };
}

/**
 * A variant in a panel: its colour and every other option chosen from the
 * values the product comes in (ADR-052) — never typed as `color=olive` — then
 * its price, stock and selling rules. Weight and size live on the Shipping tab.
 */
function VariantSheet({
  open,
  product,
  fabrics,
  variant,
  colour,
  onClose,
}: {
  open: boolean;
  product: ProductDetailDto;
  fabrics: Fabric[];
  variant: ProductVariantDto | null;
  colour: string | null;
  onClose: () => void;
}) {
  const { saving, error, addVariant, updateVariant } = useCatalogStore();
  const t = useT();
  const [draft, setDraft] = useState<Draft>(() => draftFrom(product, variant, colour));

  useEffect(() => {
    if (open) setDraft(draftFrom(product, variant, colour));
  }, [open, variant?.id, colour]);

  const set = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }));
  const colours = fabrics.filter((fabric) => fabric.color !== null);

  async function submit() {
    const options: Record<string, string> = {};
    if (draft.colour) options[COLOR_OPTION] = draft.colour;
    for (const row of draft.options) {
      const name = row.name.trim();
      const value = row.value.trim();
      if (name && value) options[name] = value;
    }
    const input = {
      sku: draft.sku.trim(),
      // null, not undefined: the API reads null as "follow the base price".
      price: draft.price.trim() ? riyalsToHalalas(draft.price) : null,
      costPrice: draft.costPrice.trim() ? riyalsToHalalas(draft.costPrice) : null,
      // Only when it was changed — an open panel must not undo a sale made meanwhile.
      ...(variant && draft.stock === String(variant.stock) ? {} : { stock: Number(draft.stock) || 0 }),
      options,
      isDefault: draft.isDefault,
      measureQuantity: product.measureUnit ? unitsToThousandths(draft.measure) : null,
      maxPerOrder: /^[1-9][0-9]{0,3}$/.test(draft.maxPerOrder.trim()) ? Number(draft.maxPerOrder.trim()) : null,
      isSample: draft.isSample,
    };
    const ok = variant ? await updateVariant(variant.id, input) : await addVariant(product.id, input);
    if (ok) onClose();
  }

  return (
    <Sheet
      open={open}
      title={variant ? t('editVariant') : t('newVariant')}
      subtitle={variant?.sku}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={saving || !draft.sku.trim()}>
            {saving ? t('saving') : t('save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        {error ? <Alert>{error}</Alert> : null}

        <fieldset className="flex flex-col gap-3">
          <legend className="mb-2 text-sm font-semibold text-ink">{t('variantOptionsLabel')}</legend>
          <Field label={t('colour')} htmlFor="variant-colour">
            <Select id="variant-colour" value={draft.colour} onChange={(event) => set({ colour: event.target.value })} className="w-full">
              <option value="">{t('noColour')}</option>
              {colours.map((fabric) => (
                <option key={fabric.key} value={fabric.color ?? ''}>
                  {fabric.label}
                </option>
              ))}
            </Select>
          </Field>
          {/* Every other option the product comes in, picked from its own values —
              a new value (a new length) is added from the Options card, where
              its variants are made for every colour at once. */}
          {draft.options.map((row, index) => {
            const axis = product.options?.find((entry) => entry.code === row.name);
            return (
              <Field key={row.name} label={axis?.name ?? row.name} htmlFor={`option-${row.name}`}>
                <Select
                  id={`option-${row.name}`}
                  value={row.value}
                  onChange={(event) =>
                    set({ options: draft.options.map((entry, at) => (at === index ? { ...entry, value: event.target.value } : entry)) })
                  }
                  className="w-full"
                >
                  {row.value === '' ? <option value="">—</option> : null}
                  {valuesOf(product, row.name).map((value) => (
                    <option key={value} value={value}>
                      {optionLabel(product, row.name, value)}
                    </option>
                  ))}
                </Select>
              </Field>
            );
          })}
        </fieldset>

        <fieldset className="grid gap-4 sm:grid-cols-2">
          <legend className="mb-2 text-sm font-semibold text-ink">{t('pricing')}</legend>
          <Field label={t('sku')} htmlFor="variant-sku">
            <Input id="variant-sku" dir="ltr" value={draft.sku} onChange={(event) => set({ sku: event.target.value })} className="w-full font-mono" />
          </Field>
          <Field label={t('price')} htmlFor="variant-price" hint={t('variantPriceHint')}>
            <Input
              id="variant-price"
              dir="ltr"
              inputMode="decimal"
              value={draft.price}
              placeholder={halalasToRiyals(product.basePrice)}
              onChange={(event) => set({ price: event.target.value })}
            />
          </Field>
          <Field label={t('variantCostPrice')} htmlFor="variant-cost" hint={t('variantCostPriceHint')}>
            <Input id="variant-cost" dir="ltr" inputMode="decimal" value={draft.costPrice} onChange={(event) => set({ costPrice: event.target.value })} />
          </Field>
          {product.kind === 'PHYSICAL' ? (
            <Field label={t('stock')} htmlFor="variant-stock" hint={t('variantStockMovement')}>
              <Input
                id="variant-stock"
                dir="ltr"
                inputMode="numeric"
                value={draft.stock}
                onChange={(event) => set({ stock: event.target.value.replace(/[^0-9]/g, '') })}
              />
            </Field>
          ) : (
            // The system keeps a digital variant's stock (ADR-077): its pool, or not counted.
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-ink">{t('stock')}</span>
              <p className="text-sm text-ink">{variant ? <DigitalStock kind={product.kind} stock={variant.stock} /> : '—'}</p>
              <p className="text-xs text-ink-muted">{product.kind === 'CODE' ? t('digitalStockCodeHint') : t('digitalStockDownloadHint')}</p>
            </div>
          )}
          {product.measureUnit ? (
            <Field
              label={t('measureHolds').replace('{unit}', t(`unit_${product.measureUnit}`))}
              htmlFor="variant-measure"
              hint={t('measureHoldsHint')}
            >
              <Input
                id="variant-measure"
                dir="ltr"
                inputMode="decimal"
                value={draft.measure}
                onChange={(event) => set({ measure: event.target.value })}
              />
            </Field>
          ) : null}
        </fieldset>

        <fieldset className="flex flex-col gap-3">
          <legend className="mb-2 text-sm font-semibold text-ink">{t('variantSelling')}</legend>
          <Field label={t('maxPerOrder')} htmlFor="variant-max" hint={t('maxPerOrderHint')}>
            <Input
              id="variant-max"
              dir="ltr"
              inputMode="numeric"
              value={draft.maxPerOrder}
              onChange={(event) => set({ maxPerOrder: event.target.value.replace(/[^0-9]/g, '') })}
              className="w-32"
            />
          </Field>
          <ToggleField
            label={t('sampleVariant')}
            hint={t('sampleVariantHint')}
            checked={draft.isSample}
            onChange={(isSample) => set({ isSample })}
          />
          <ToggleField label={t('defaultVariant')} checked={draft.isDefault} onChange={(isDefault) => set({ isDefault })} />
        </fieldset>
      </div>
    </Sheet>
  );
}

/** A digital variant's stock as staff read it (ADR-077): codes left, or not counted. */
function DigitalStock({ kind, stock }: { kind: ProductDetailDto['kind']; stock: number }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  if (kind === 'DOWNLOAD') return <span className="text-sm text-ink-muted">{t('notCounted')}</span>;
  return (
    <span className={cx('text-sm', stock === 0 ? 'text-danger' : 'text-ink')}>
      {t('codesLeft', { count: count(stock, locale) })}
    </span>
  );
}
